import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import AdminLayout from './AdminLayout';
import StatusBadge from '@/components/common/StatusBadge';
import { 
  getBuildings, getFloorsByBuilding, getRoomsByFloor, getBedSpacesByRoom,
  createRoom, createBedSpace, updateBedSpace, deactivateBedSpace 
} from '@/services/nocodeBackendService';

const { FiChevronRight, FiPlus, FiBox, FiLayers, FiMonitor, FiUser, FiEdit2, FiTrash2, FiSave } = FiIcons;

const PropertyStructure = () => {
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bedSpaces, setBedSpaces] = useState([]);
  
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingBed, setEditingBed] = useState(null);
  const [editStatus, setEditStatus] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const bData = await getBuildings();
      setBuildings(bData);
      
      if (selectedBuilding) {
        const fData = await getFloorsByBuilding(selectedBuilding.id);
        setFloors(fData);
      }
      if (selectedFloor) {
        const rData = await getRoomsByFloor(selectedFloor.id);
        setRooms(rData);
      }
      if (selectedRoom) {
        const bedData = await getBedSpacesByRoom(selectedRoom.id);
        setBedSpaces(bedData);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding, selectedFloor, selectedRoom]);

  const handleAddDemoRoom = async () => {
    if (!selectedFloor) return;
    setActionLoading(true);
    try {
      await createRoom({
        building_id: selectedBuilding.id,
        floor_id: selectedFloor.id,
        room_name: `Demo Room ${rooms.length + 1}`,
        room_number: `10${rooms.length + 1}`,
      });
      await loadData();
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDemoBedSpace = async () => {
    if (!selectedRoom) return;
    setActionLoading(true);
    try {
      await createBedSpace({
        building_id: selectedBuilding.id,
        floor_id: selectedFloor.id,
        room_id: selectedRoom.id,
        bed_space_name: `Bed ${String.fromCharCode(65 + bedSpaces.length)}`,
        bed_space_number: String.fromCharCode(65 + bedSpaces.length),
        monthly_rate: selectedBuilding.monthly_rate,
      });
      await loadData();
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveBedStatus = async (bedId) => {
    setActionLoading(true);
    try {
      await updateBedSpace(bedId, { status: editStatus });
      setEditingBed(null);
      await loadData();
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateBed = async (bedId) => {
    if (!window.confirm('Are you sure you want to deactivate this bed space?')) return;
    setActionLoading(true);
    try {
      await deactivateBedSpace(bedId);
      await loadData();
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Property Structure</h1>
        <p className="text-slate-600 mt-1">Drill down through your property hierarchy to manage rooms and bed spaces.</p>
      </div>

      {/* Drill-down Navigation Header */}
      <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-white rounded-xl shadow-sm border border-slate-200 text-sm font-medium text-slate-600">
        <button 
          onClick={() => { setSelectedBuilding(null); setSelectedFloor(null); setSelectedRoom(null); }}
          className={`hover:text-blue-600 ${!selectedBuilding ? 'text-blue-600' : ''}`}
        >
          Buildings
        </button>
        {selectedBuilding && (
          <>
            <SafeIcon icon={FiChevronRight} className="text-slate-400" />
            <button 
              onClick={() => { setSelectedFloor(null); setSelectedRoom(null); }}
              className={`hover:text-blue-600 ${!selectedFloor ? 'text-blue-600' : ''}`}
            >
              {selectedBuilding.name}
            </button>
          </>
        )}
        {selectedFloor && (
          <>
            <SafeIcon icon={FiChevronRight} className="text-slate-400" />
            <button 
              onClick={() => setSelectedRoom(null)}
              className={`hover:text-blue-600 ${!selectedRoom ? 'text-blue-600' : ''}`}
            >
              {selectedFloor.floor_name}
            </button>
          </>
        )}
        {selectedRoom && (
          <>
            <SafeIcon icon={FiChevronRight} className="text-slate-400" />
            <span className="text-blue-600">{selectedRoom.room_name}</span>
          </>
        )}
      </div>

      {loading && !actionLoading ? (
        <div className="p-12 text-center text-slate-500">Loading data...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          
          {/* LEVEL 1: BUILDINGS */}
          {!selectedBuilding && (
            <div className="divide-y divide-slate-100">
              {buildings.map(b => (
                <div key={b.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="bg-sky-50 p-2 rounded text-sky-600"><SafeIcon icon={FiBox} /></div>
                    <div>
                      <h3 className="font-bold text-slate-800">{b.name}</h3>
                      <p className="text-xs text-slate-500">₱{b.monthly_rate} / month</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedBuilding(b)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded hover:bg-slate-200 transition-colors"
                  >
                    View Floors
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* LEVEL 2: FLOORS */}
          {selectedBuilding && !selectedFloor && (
            <div className="divide-y divide-slate-100">
              {floors.map(f => (
                <div key={f.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="bg-slate-100 p-2 rounded text-slate-600"><SafeIcon icon={FiLayers} /></div>
                    <h3 className="font-bold text-slate-800">{f.floor_name}</h3>
                  </div>
                  <button 
                    onClick={() => setSelectedFloor(f)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded hover:bg-slate-200 transition-colors"
                  >
                    View Rooms
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* LEVEL 3: ROOMS */}
          {selectedFloor && !selectedRoom && (
            <div>
              <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <h3 className="font-bold text-slate-700">Rooms on {selectedFloor.floor_name}</h3>
                <button 
                  onClick={handleAddDemoRoom}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  <SafeIcon icon={FiPlus} /> Add Demo Room
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {rooms.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No rooms found. Add a demo room to start.</div>
                ) : rooms.map(r => (
                  <div key={r.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className="bg-slate-100 p-2 rounded text-slate-600"><SafeIcon icon={FiMonitor} /></div>
                      <div>
                        <h3 className="font-bold text-slate-800">{r.room_name}</h3>
                        {r.is_demo && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Demo</span>}
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedRoom(r)}
                      className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded hover:bg-slate-200 transition-colors"
                    >
                      Manage Bed Spaces
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LEVEL 4: BED SPACES */}
          {selectedRoom && (
            <div>
              <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <h3 className="font-bold text-slate-700">Bed Spaces in {selectedRoom.room_name}</h3>
                <button 
                  onClick={handleAddDemoBedSpace}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  <SafeIcon icon={FiPlus} /> Add Demo Bed Space
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {bedSpaces.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No bed spaces found. Add one to start.</div>
                ) : bedSpaces.map(bed => (
                  <div key={bed.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-slate-50 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="bg-sky-100 p-2 rounded text-sky-600"><SafeIcon icon={FiUser} /></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-slate-800">{bed.bed_space_name}</h3>
                          {bed.is_demo && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Demo</span>}
                        </div>
                        <p className="text-xs text-slate-500">Rate: ₱{bed.monthly_rate}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {editingBed === bed.id ? (
                        <div className="flex items-center gap-2">
                          <select 
                            className="text-sm border-slate-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                          >
                            <option value="available">Available</option>
                            <option value="occupied">Occupied</option>
                            <option value="reserved">Reserved</option>
                            <option value="maintenance">Maintenance</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          <button 
                            onClick={() => handleSaveBedStatus(bed.id)}
                            className="p-1.5 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                            title="Save Status"
                            disabled={actionLoading}
                          >
                            <SafeIcon icon={FiSave} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <StatusBadge status={bed.status} />
                          <button 
                            onClick={() => { setEditingBed(bed.id); setEditStatus(bed.status); }}
                            className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                            title="Edit Status"
                            disabled={actionLoading}
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button 
                            onClick={() => handleDeactivateBed(bed.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors"
                            title="Deactivate"
                            disabled={actionLoading || bed.status === 'inactive'}
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </AdminLayout>
  );
};

export default PropertyStructure;